import React from 'react';
import { Bug, Zap, FileText, BookOpen, CheckSquare, User, Calendar, Flag, MessageCircle, Clock } from 'lucide-react';
import { UserAvatar } from '../UI/UserAvatar';
import { Issue, User as UserType } from '../../types';

interface IssueCardProps {
  issue: Issue;
  users: UserType[];
  onClick?: () => void;
  onMessageClick?: () => void;
  compact?: boolean;
}

export const IssueCard: React.FC<IssueCardProps> = ({ issue, users, onClick, onMessageClick, compact = false }) => {
  const assignee = users.find(u => u.id in issue.assignees_users);
  const reporter = users.find(u => u.id === issue.reporter);

  const getIssueTypeIcon = (type: string) => {
    switch (type) {
      case 'bug':
        return <Bug className="w-4 h-4 text-red-600" />;
      case 'story':
        return <Zap className="w-4 h-4 text-green-600" />;
      case 'task':
        return <CheckSquare className="w-4 h-4 text-blue-600" />;
      case 'epic':
        return <BookOpen className="w-4 h-4 text-purple-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'to-do':
      case 'backlog':
        return 'bg-gray-100 text-gray-800';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800';
      case 'in-review':
        return 'bg-purple-100 text-purple-800';
      case 'on-hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'done':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'highest':
        return 'text-red-600';
      case 'high':
        return 'text-orange-600';
      case 'medium':
        return 'text-yellow-600';
      case 'low':
        return 'text-green-600';
      case 'lowest':
        return 'text-gray-600';
      default:
        return 'text-gray-600';
    }
  };

  const isOverdue = issue.dueDate && new Date(issue.dueDate) < new Date() && issue.status !== 'done';

  if (compact) {
    return (
      <div
        className="bg-white/90 backdrop-blur-sm border border-gray-200/50 rounded-xl p-4 hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-200 cursor-pointer group hover:border-blue-300/50"
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {getIssueTypeIcon(issue.type)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-sm font-bold text-blue-600 group-hover:text-blue-700">{issue.key}</span>
              <Flag className={`w-3 h-3 ${getPriorityColor(issue.priority)}`} />
            </div>
            <p
              onClick={onClick}
              className="text-xs text-black line-clamp-2 font-bold group-hover:text-brand-700 cursor-pointer"
            >
              {issue.summary}
            </p>
            <div className="flex items-center justify-between mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(issue.status)}`}>
                {issue.status.replace('-', ' ').toUpperCase()}
              </span>
              <div className="flex items-center space-x-2">
                {onMessageClick && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMessageClick();
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Open chat"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                )}
                {assignee && (
                  <UserAvatar
                    name={assignee.displayName}
                    src={assignee.avatarUrl}
                    className="w-6 h-6 border-2 border-white shadow-sm"
                  />
                )}
              </div>
            </div>
            <div className="mt-1 text-xs text-gray-500 flex items-center space-x-1">
              <Clock className="w-3 h-3" />
              <span>{new Date(issue.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white/95 backdrop-blur-sm border border-gray-200/50 rounded-xl p-5 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-200 cursor-pointer group hover:border-blue-300/50"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          {getIssueTypeIcon(issue.type)}
          <span className="text-sm font-medium text-blue-600">{issue.key}</span>
          <Flag className={`w-4 h-4 ${getPriorityColor(issue.priority)}`} />
        </div>
        <div className="flex items-center space-x-2">
          {onMessageClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMessageClick();
              }}
              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Open chat"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
          )}
          {isOverdue && (
            <span className="text-xs text-red-600 font-bold bg-red-50 px-3 py-1 rounded-full shadow-sm">
              Overdue
            </span>
          )}
        </div>
      </div>

      <h4
        onClick={onClick}
        className="text-xs font-bold text-black mb-3 line-clamp-2 group-hover:text-brand-700 cursor-pointer"
      >
        {issue.summary}
      </h4>

      {issue.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{issue.description}</p>
      )}

      <div className="flex items-center justify-between mb-3">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(issue.status)}`}>
          {issue.status.replace('-', ' ').toUpperCase()}
        </span>
        {issue.estimationTime && (
          <span className="text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full font-medium shadow-sm">
            {issue.estimationTime}h est.
          </span>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{new Date(issue.createdAt).toLocaleDateString()}</span>
          </div>
          {issue.dueDate && (
            <div className="flex items-center space-x-1">
              <Calendar className="w-3 h-3" />
              <span>{new Date(issue.dueDate).toLocaleDateString()}</span>
            </div>
          )}
          {reporter && (
            <div className="flex items-center space-x-1">
              <span>Reporter:</span>
              <UserAvatar
                name={reporter.displayName}
                src={reporter.avatarUrl}
                className="w-4 h-4 border border-white shadow-sm"
              />
            </div>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {issue.assignees.length > 0 ? (
            <div className="flex -space-x-1">
              {issue.assignees.slice(0, 3).map(assigneeId => {
                const user = users.find(u => u.id === assigneeId);
                return user ? (
                  <UserAvatar
                    key={user.id}
                    name={user.displayName}
                    src={user.avatarUrl}
                    className="w-6 h-6 border-2 border-white shadow-sm"
                  />
                ) : null;
              })}
              {issue.assignees.length > 3 && (
                <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                  +{issue.assignees.length - 3}
                </div>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">Unassigned</span>
          )}
        </div>
      </div>

      {/* Show primary assignee for backward compatibility */}
      {assignee && issue.assignees.length === 1 && (
        <div className="flex items-center space-x-1">
          <UserAvatar
            name={assignee.displayName}
            src={assignee.avatarUrl}
            className="w-7 h-7 border-2 border-white shadow-md"
          />
          <span className="font-medium">{assignee.displayName.split(' ')[0]}</span>
        </div>
      )}

      {issue.labels.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {issue.labels.slice(0, 3).map((label) => (
            <span
              key={label}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 shadow-sm"
            >
              {label}
            </span>
          ))}
          {issue.labels.length > 3 && (
            <span className="text-xs text-gray-500">+{issue.labels.length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
};
